import React, { useState } from 'react';
import toast from 'react-hot-toast';
import tmrAccounts from '@redux/modules/tmrAccounts';
import styles from './upgradeForm.module.scss';

const PLANS = {
    business: {
        label: 'Business',
        price: '$49/month',
        clients: 5,
        features: ['Up to 5 clients', 'Jobs embed', 'Ghost CMS sync', 'Team access'],
    },
    enterprise: {
        label: 'Enterprise',
        price: '$149/month',
        clients: 'Unlimited',
        features: [
            'Unlimited clients',
            'Jobs embed',
            'Ghost CMS sync',
            'Team access',
            'Priority support',
            'Custom integrations',
        ],
    },
};

export const UpgradeForm = ({ account, onUpgraded }) => {
    const [selectedPlan, setSelectedPlan] = useState('business');
    const [orgName, setOrgName] = useState('');
    const [billingName, setBillingName] = useState('');
    const [billingEmail, setBillingEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [upgradeAccount] = tmrAccounts.form().useTmrAccountupgradeMutation();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!orgName.trim()) {
            toast.error('Please enter an organization name.');
            return;
        }

        setSubmitting(true);
        try {
            const result = await upgradeAccount({
                body: {
                    user_id: account?.owner_user_id,
                    level: selectedPlan,
                    org_name: orgName.trim(),
                    billing_name: billingName.trim(),
                    billing_email: billingEmail.trim(),
                },
            }).unwrap();

            if (result?.success) {
                toast.success(
                    result?.messages ||
                    `Your ${PLANS[selectedPlan].label} upgrade request has been submitted for admin approval.`
                );
                setSubmitted(true);
            } else {
                const err = result?.errors || result?.message || 'Upgrade request failed.';
                toast.error(Array.isArray(err) ? err.join(', ') : String(err));
            }
        } catch (err) {
            const e = err?.data?.errors || err?.data?.message || 'Upgrade request failed. Please try again.';
            toast.error(Array.isArray(e) ? e.join(', ') : String(e));
        } finally {
            setSubmitting(false);
        }
    };

    if (account?.account_level !== 'free') {
        return null;
    }

    return (
        <div className={styles.wrapper}>
            <h3>Upgrade Your Account</h3>
            <div className={styles.upgradeCard}>
                <p>
                    Request an upgrade to unlock more clients, jobs embed, team access,
                    and organization management. An admin will review and approve your
                    request.
                </p>

                {submitted && (
                    <div className={styles.success}>
                        Your upgrade request has been submitted and is awaiting admin approval.
                        You&apos;ll be upgraded once it&apos;s approved.
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className={styles.plans}>
                        {Object.entries(PLANS).map(([key, plan]) => (
                            <div
                                key={key}
                                className={`${styles.planOption} ${selectedPlan === key ? styles.selected : ''
                                    }`}
                                onClick={() => setSelectedPlan(key)}
                            >
                                <h4>{plan.label}</h4>
                                <div className={styles.price}>{plan.price}</div>
                                <ul className={styles.features}>
                                    {plan.features.map((f) => (
                                        <li key={f}>{f}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <div className={styles.formGroup}>
                        <label>Organization Name *</label>
                        <input
                            type="text"
                            value={orgName}
                            onChange={(e) => setOrgName(e.target.value)}
                            placeholder="e.g., Acme Recruitment"
                            required
                        />
                    </div>

                    <div className={styles.billingSection}>
                        <h4>Billing Details</h4>
                        <div className={styles.formGroup}>
                            <label>Billing Contact Name</label>
                            <input
                                type="text"
                                value={billingName}
                                onChange={(e) => setBillingName(e.target.value)}
                                placeholder="Full name for billing"
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Billing Email</label>
                            <input
                                type="email"
                                value={billingEmail}
                                onChange={(e) => setBillingEmail(e.target.value)}
                                placeholder="billing@example.com"
                            />
                        </div>
                    </div>

                    <div className={styles.buttonRow}>
                        <button
                            type="submit"
                            className={styles.primaryBtn}
                            disabled={submitting || submitted || !orgName.trim()}
                        >
                            {submitting
                                ? 'Submitting...'
                                : submitted
                                    ? 'Request Submitted'
                                    : `Request ${PLANS[selectedPlan].label} Upgrade`}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
